import * as React from 'react'

import { EDocumentType } from '@/utils/constants'

import { useAllVersionsReady } from '../../hooks'
import { useMinimapMarkers } from '../../html/hooks'
import { IHTMLCompareChangeItem } from '../../html/types'
import { usePdfMinimapMarkers } from '../../pdf/hooks'
import { IPDFCompareChangeItem } from '../../pdf/types'
import { useVersionComparisonStore } from '../../store'

/**
 * Minimap component that displays a visual representation of document differences
 * Shows two columns: left for deletions (source version), right for insertions (target version)
 */
const ComparisonMinimap: React.FC = () => {
  const {
    compareDocumentDiff,
    compareVersions,
    documentType,
    currentPage,
    zoomScale,
  } = useVersionComparisonStore()
  const minimapRef = React.useRef<HTMLButtonElement>(null)
  // Ratios keep columns proportional to each version layout.
  // For PDF, ratios are based on page counts.
  const [layoutMetrics, setLayoutMetrics] = React.useState({
    sourceHeightRatio: 100,
    targetHeightRatio: 100,
    sourcePageCount: 1,
    targetPageCount: 1,
  })
  const [scrollBoxStyle, setScrollBoxStyle] = React.useState<{
    topPercent: number
    heightPercent: number
    visible: boolean
  }>({
    topPercent: 0,
    heightPercent: 0,
    visible: false,
  })

  // Check if all versions are ready (required for zoom and scroll features)
  const allVersionsReady = useAllVersionsReady()

  // Pick which layout drives the minimap scroll box.
  // Prefer current version, then previous, then any ready/available version.
  const scrollTargetVersionId = React.useMemo(() => {
    if (!compareVersions || compareVersions.length === 0) return null

    const currentVersion = compareVersions.find((version) => version.isTarget)
    if (currentVersion?.id) return currentVersion.id

    const previousVersion = compareVersions.find((version) => version.isSource)
    if (previousVersion?.id) return previousVersion.id

    const readyVersion = compareVersions.find((version) => version.isReady)
    return readyVersion?.id ?? compareVersions[0]?.id ?? null
  }, [compareVersions])

  const sourceVersionId = React.useMemo(() => {
    return compareVersions?.find((version) => version.isSource)?.id ?? null
  }, [compareVersions])

  const targetVersionId = React.useMemo(() => {
    return compareVersions?.find((version) => version.isTarget)?.id ?? null
  }, [compareVersions])

  const sourceTotalPages = React.useMemo(() => {
    return compareVersions?.find((version) => version.isSource)?.totalPages
  }, [compareVersions])

  const targetTotalPages = React.useMemo(() => {
    return compareVersions?.find((version) => version.isTarget)?.totalPages
  }, [compareVersions])

  const isHtmlViewer = React.useMemo(
    () => documentType === EDocumentType.HTML,
    [documentType]
  )
  const isPdfViewer = React.useMemo(
    () => documentType === EDocumentType.PDF,
    [documentType]
  )

  // Translate the layout's scroll state into minimap percentages.
  const updateScrollBox = React.useCallback(() => {
    if (!scrollTargetVersionId) {
      setScrollBoxStyle({ topPercent: 0, heightPercent: 0, visible: false })
      return
    }

    // PDF logic
    if (isPdfViewer) {
      const pageCount = Math.max(
        layoutMetrics.sourcePageCount,
        layoutMetrics.targetPageCount,
        1
      )
      const clampedPage = Math.min(Math.max(currentPage, 1), pageCount)
      const heightPercent = 100 / pageCount
      const topPercent = ((clampedPage - 1) / pageCount) * 100

      setScrollBoxStyle({ topPercent, heightPercent, visible: pageCount > 1 })
      return
    }

    // HTML logic
    const scrollContainer = document.getElementById(scrollTargetVersionId)
    if (!scrollContainer) {
      setScrollBoxStyle({ topPercent: 0, heightPercent: 0, visible: false })
      return
    }

    const scrollHeight = scrollContainer.scrollHeight
    const clientHeight = scrollContainer.clientHeight

    if (scrollHeight <= 0 || clientHeight <= 0) {
      setScrollBoxStyle({ topPercent: 0, heightPercent: 0, visible: false })
      return
    }

    const heightPercent = (clientHeight / scrollHeight) * 100
    const topPercent = (scrollContainer.scrollTop / scrollHeight) * 100

    setScrollBoxStyle({
      topPercent,
      heightPercent,
      visible: heightPercent < 100,
    })
  }, [
    currentPage,
    isPdfViewer,
    layoutMetrics.sourcePageCount,
    layoutMetrics.targetPageCount,
    scrollTargetVersionId,
  ])

  const { diffMarkers: htmlDiffMarkers, refreshMarkers: refreshHtmlMarkers } =
    useMinimapMarkers({
      compareDocumentDiff: isHtmlViewer
        ? (compareDocumentDiff as IHTMLCompareChangeItem[] | null)
        : null,
      sourceVersionId,
      targetVersionId,
      isEnabled: isHtmlViewer,
    })

  const {
    diffMarkers: pdfDiffMarkers,
    refreshMarkers: refreshPdfMarkers,
    layoutMetrics: pdfLayoutMetrics,
  } = usePdfMinimapMarkers({
    compareDocumentDiff: isPdfViewer
      ? (compareDocumentDiff as IPDFCompareChangeItem[] | null)
      : null,
    sourceTotalPages,
    targetTotalPages,
    isEnabled: isPdfViewer,
  })

  const updateLayoutMetrics = React.useCallback(() => {
    if (!isHtmlViewer) return

    const sourceScrollHeight = sourceVersionId
      ? (document.getElementById(sourceVersionId)?.scrollHeight ?? 0)
      : 0
    const targetScrollHeight = targetVersionId
      ? (document.getElementById(targetVersionId)?.scrollHeight ?? 0)
      : 0

    const maxHeight = Math.max(sourceScrollHeight, targetScrollHeight, 1)

    // Scale columns to the tallest layout to keep ratios aligned.
    setLayoutMetrics({
      sourceHeightRatio: (sourceScrollHeight / maxHeight) * 100,
      targetHeightRatio: (targetScrollHeight / maxHeight) * 100,
      sourcePageCount: 1,
      targetPageCount: 1,
    })
  }, [isHtmlViewer, sourceVersionId, targetVersionId])

  // Keep layout metrics in sync for PDF based on page counts.
  React.useEffect(() => {
    if (!isPdfViewer) return
    setLayoutMetrics(pdfLayoutMetrics)
  }, [isPdfViewer, pdfLayoutMetrics])

  /**
   * Update scroll box and refresh markers
   * This effect runs when the scroll target version ID changes
   * and when the all versions are ready
   * This effect is used to keep the scroll box synced with the layout scroll/resize.
   */
  React.useEffect(() => {
    // If not all versions are ready, don't update the scroll box
    if (!allVersionsReady) return

    // If no scroll target version ID, don't update the scroll box
    if (!scrollTargetVersionId) return

    // If not an HTML viewer, don't update the scroll box
    if (!isHtmlViewer) return

    const scrollContainer = document.getElementById(scrollTargetVersionId)
    if (!scrollContainer) return

    // Keep the scroll box synced with layout scroll/resize.
    let rafId: number | null = null
    const handleScroll = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updateScrollBox)
    }

    updateScrollBox()
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [allVersionsReady, isHtmlViewer, scrollTargetVersionId, updateScrollBox])

  // Keep PDF scroll box in sync with current page and layout metrics.
  React.useEffect(() => {
    if (!allVersionsReady) return
    if (!isPdfViewer) return
    updateScrollBox()
  }, [
    allVersionsReady,
    isPdfViewer,
    currentPage,
    layoutMetrics,
    updateScrollBox,
  ])

  /**
   * Update layout metrics and refresh markers
   * This effect runs when the window is resized
   * This effect is used to keep the layout metrics synced with the layout scroll/resize.
   */
  React.useEffect(() => {
    let rafId: number | null = null

    const handleUpdate = () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        if (isHtmlViewer) {
          updateLayoutMetrics()
          refreshHtmlMarkers()
        }

        if (isPdfViewer) refreshPdfMarkers()
      })
    }

    handleUpdate()
    window.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('resize', handleUpdate)
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [
    isHtmlViewer,
    isPdfViewer,
    refreshHtmlMarkers,
    refreshPdfMarkers,
    updateLayoutMetrics,
  ])

  // Recalculate scroll box/markers when zoom changes (HTML only).
  React.useEffect(() => {
    if (!allVersionsReady) return
    if (!isHtmlViewer) return

    const rafId = requestAnimationFrame(() => {
      updateLayoutMetrics()
      refreshHtmlMarkers()
      updateScrollBox()
    })

    return () => cancelAnimationFrame(rafId)
  }, [
    allVersionsReady,
    isHtmlViewer,
    refreshHtmlMarkers,
    updateLayoutMetrics,
    updateScrollBox,
    zoomScale,
  ])

  const handleMinimapClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Map click position to scroll position and center the viewport.
      if (!scrollTargetVersionId) return

      const scrollContainer = document.getElementById(scrollTargetVersionId)
      const minimapElement = minimapRef.current
      if (!scrollContainer || !minimapElement) return

      const rect = minimapElement.getBoundingClientRect()
      if (rect.height === 0) return

      const clickOffset = event.clientY - rect.top
      const clickRatio = Math.min(Math.max(clickOffset / rect.height, 0), 1)

      const scrollHeight = scrollContainer.scrollHeight
      const clientHeight = scrollContainer.clientHeight
      const maxScrollTop = Math.max(0, scrollHeight - clientHeight)
      const targetScrollTop = Math.min(
        maxScrollTop,
        Math.max(0, clickRatio * scrollHeight - clientHeight / 2)
      )

      scrollContainer.scrollTo({
        top: targetScrollTop,
        left: scrollContainer.scrollLeft,
        behavior: 'smooth',
      })
    },
    [scrollTargetVersionId]
  )

  if (!compareDocumentDiff) {
    return (
      <div className="flex-shrink-0 h-full overflow-hidden w-12 bg-primary rounded-md drop-shadow" />
    )
  }

  return (
    <button
      type="button"
      ref={minimapRef}
      className="flex-shrink-0 h-full overflow-hidden w-12 bg-primary rounded-md drop-shadow flex relative cursor-pointer border-0 p-0"
      onClick={handleMinimapClick}
      aria-label="Scroll document position"
    >
      {/* Source version column (left) - shows deletions */}
      <div className="flex-1 relative">
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: `${layoutMetrics.sourceHeightRatio.toFixed(2)}%` }}
        >
          {(isPdfViewer ? pdfDiffMarkers : htmlDiffMarkers)?.sourceMarkers?.map(
            (marker) => (
              <div
                key={marker.key}
                className="absolute left-0 right-0 bg-brand-utility-error-100"
                style={{
                  top: `${marker.topPercent.toFixed(2)}%`,
                  height: `${marker.heightPercent.toFixed(2)}%`,
                }}
              />
            )
          )}
        </div>
      </div>

      {/* Target version column (right) - shows insertions */}
      <div className="flex-1 relative">
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: `${layoutMetrics.targetHeightRatio.toFixed(2)}%` }}
        >
          {(isPdfViewer ? pdfDiffMarkers : htmlDiffMarkers)?.targetMarkers?.map(
            (marker) => (
              <div
                key={marker.key}
                className="absolute left-0 right-0 bg-brand-utility-brand-100_alt"
                style={{
                  top: `${marker.topPercent.toFixed(2)}%`,
                  height: `${marker.heightPercent.toFixed(2)}%`,
                }}
              />
            )
          )}
        </div>
      </div>

      {/* Scroll box showing current viewport position/size */}
      {scrollBoxStyle.visible && (
        <div
          className="absolute left-0 right-0 bg-gray-500 opacity-20 rounded-xs pointer-events-none"
          style={{
            top: `${scrollBoxStyle.topPercent.toFixed(2)}%`,
            height: `${scrollBoxStyle.heightPercent.toFixed(2)}%`,
          }}
        />
      )}
    </button>
  )
}

const ComparisonLayoutMinimapMemoized = React.memo(ComparisonMinimap)
ComparisonLayoutMinimapMemoized.displayName = 'ComparisonMinimap'

export { ComparisonLayoutMinimapMemoized as ComparisonMinimap }
